import * as React from 'react';
import { connect } from 'react-redux';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';

import { getLanguageFields } from '../../actions/EditorActions/EditorActions';
import { Loader } from '../../components/Loader';
import { LearnSideBar } from '../../components/Learn/';
import { addModifiers } from '../../shared/helpers';
import { DispatchConnect, StoreType } from '../../types';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { LEARN_PART_PATH_OPERATORS } from '../../shared/constants';
import { LearnScopeContext } from './LearnScopeContext';

const LearnMain = React.lazy(() => import('../../components/Learn/LearnMain')) as any;
const LearnCourses = React.lazy(() => import('./LearnCourses')) as any;
const LearnTutorials = React.lazy(() => import('./LearnTutorials')) as any;
const LearnReferences = React.lazy(() => import('./LearnReferences')) as any;
const LearnSearch = React.lazy(() => import('./Search/LearnSearch')) as any;

interface LearnProps {
  // Scope is to decide where the Learn component placed.
  // We should use scope to do styling or adding logic based on where the component displayed.
  scope?: 'learn' | 'simulate';
  // Path is the base path for every links inside the Learn component
  path?: string;
  navHasText?: boolean;
  hasHero?: boolean;
  homeLink?: boolean;
  hasTitle?: boolean;
  scrollElement?: string;
  // This function will handle the clone alpha when the user is on the simulate page
  handleSimulateClone?: (alpha: string) => void;
  handleShowModal?: () => boolean;
  fullPlaylist?: boolean;
  isMultiTab?: boolean;
  fullWidth?: boolean;
}

interface LearnStateDispatchers {
  handleGetLanguageFields: () => void;
}

type LearnGenericType = LearnProps & RouteComponentProps<{}> & LearnStateDispatchers;

const Learn = (props: LearnGenericType) => {
  const {
    scope = 'learn',
    path = 'learn',
    navHasText = true,
    homeLink = false,
    hasHero = true,
    fullPlaylist = false,
    hasTitle = false,
    isMultiTab = false,
    scrollElement,
    handleSimulateClone,
    handleShowModal,
    fullWidth = true,
    handleGetLanguageFields,
  } = props;

  React.useEffect(() => {
    handleGetLanguageFields();
  }, []);

  return (
    <LearnScopeContext.Provider value={scope}>
      <ErrorBoundary showUI={true} isNotFullScreen={true}>
        <section className={addModifiers('learn', fullWidth === true ? ['full-width'] : [])}>
          <LearnSideBar scope={scope} path={path} navHasText={navHasText} homeLink={homeLink} />
          <div className="learn__container">
            <React.Suspense fallback={<Loader active={true} inline="centered" size="massive" />}>
              <Switch>
                <Route exact={true} path={`/${path}`}>
                  <LearnMain path={path} hasHero={hasHero} hasTitle={hasTitle} />
                </Route>
                <Route path={`/${path}/courses`}>
                  <LearnCourses path={path} fullPlaylist={fullPlaylist} />
                </Route>
                <Route path={`/${path}/documentation`}>
                  <LearnTutorials
                    path={path}
                    scrollElement={scrollElement}
                    handleSimulateClone={handleSimulateClone}
                    handleShowModal={handleShowModal}
                    isMultiTab={isMultiTab}
                  />
                </Route>
                <Route path={`/${path}/${LEARN_PART_PATH_OPERATORS}`}>
                  <LearnReferences path={path} />
                </Route>
                <Route path={`/${path}/search`}>
                  <LearnSearch path={path} {...props} />
                </Route>
              </Switch>
            </React.Suspense>
          </div>
        </section>
      </ErrorBoundary>
    </LearnScopeContext.Provider>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<any>): LearnStateDispatchers => ({
  handleGetLanguageFields: () => {
    dispatch(getLanguageFields({}));
  },
});

export default withRouter(connect<any, DispatchConnect<LearnStateDispatchers>, any, StoreType>(null, mapDispatchToProps)(Learn));
